import { chakra, Flex, Text } from '@chakra-ui/react';
import React from 'react';

import type { Block } from 'types/api/block';

import getBlockTotalReward from 'lib/block/getBlockTotalReward';
import { currencyUnits } from 'lib/units';
import { Badge } from 'toolkit/chakra/badge';
import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import IconSvg from 'ui/shared/IconSvg';
import TimeWithTooltip from 'ui/shared/time/TimeWithTooltip';

type Props = {
  block: Block;
  isLoading?: boolean;
  animation?: string;
};

const LatestBlocksItem = ({ block, isLoading, animation }: Props) => {
  const totalReward = getBlockTotalReward(block);
  return (
    <Flex
      animation={ animation }
      direction="row"
      alignItems="center"
      w="100%"
      justifyContent="space-between"
      borderBottom="1px solid"
      borderColor="border.divider"
      p=".9rem"
    >
      <Flex alignItems="center" overflow="hidden" columnGap={ 3 } flex="0 0 200px">
        <IconSvg
          bg={{ _light: 'gray.100', _dark: 'gray.900' }}
          name="block"
          boxSize={ 10 }
          p="4px"
          pr="2px"
          isLoading={ isLoading }
          flexShrink={ 0 }
          borderRadius="base"
        />
        <Flex direction="column" alignItems="flex-start" overflow="hidden">
          <Link loading={ isLoading } href={ `/block/${ block.height }` }>
            <Text fontWeight={ 600 } fontSize="sm" color="link.primary">
              { block.height }
            </Text>
          </Link>
          <TimeWithTooltip
            timestamp={ block.timestamp }
            enableIncrement={ !isLoading }
            timeFormat="relative"
            isLoading={ isLoading }
            color="text.secondary"
            display="inline-block"
            textStyle="xs"
            flexShrink={ 0 }
          />
        </Flex>
      </Flex>

      <Flex direction="column" flex="1" minW="0" rowGap={ 0 }>
        <Flex columnGap={ 2 } alignItems="center">
          <Skeleton loading={ isLoading } fontWeight={ 500 } color="text.secondary" textStyle="xs">Miner</Skeleton>
          <AddressEntity
            address={ block.miner }
            isLoading={ isLoading }
            noIcon
            noCopy
            truncation="constant"
            textStyle="xs"
          />
        </Flex>
        <Flex columnGap={ 1 } alignItems="center" justifyContent="flex-start">
          <Skeleton loading={ isLoading } fontWeight={ 500 } color="text.secondary" textStyle="xs">Txn</Skeleton>
          <Link loading={ isLoading } href={ `/block/${ block.height }?tab=txs` }>
            <Skeleton loading={ isLoading } color="link.primary" textStyle="xs">
              <chakra.span >{ block.transactions_count }</chakra.span>
            </Skeleton>
          </Link>
        </Flex>
      </Flex>

      <Flex alignItems="center" justifyContent="flex-end" flex="0 0 150px">
        <Skeleton loading={ isLoading } textStyle="xs">
          <Badge variant="surface" fontSize="xs" textStyle="xs">
            { totalReward.dp(4).toFixed() } { currencyUnits.ether }
          </Badge>
        </Skeleton>
      </Flex>

    </Flex>
  );
};

export default LatestBlocksItem;
